/**
 * Scores a Get Matched brief against the user's BuildHub projects so the
 * modal can suggest pre-filling from one. The matcher runs after the
 * ProjectScope step where only Region and ProjectScope are populated, so the
 * 2-field threshold effectively requires both to match — a strict gate.
 */
import { BUDGET_OPTIONS } from '@modules/build-hub-creator/configs/constants';
import { PROJECT_SCOPE_LABELS } from '~/ui-rocco/pages/category-page/GetMatchedModal/Step4ProjectScope';

export interface MatchableProject {
    ID: number;
    Title: string;
    ProjectScope: string | null;
    Location: string | null;
    LocationData: {
        FullAddress: string | null;
        Suburb: string | null;
        City: string | null;
        Region: string | null;
    } | null;
    Budget: {
        Min: number | null;
        Max: number | null;
    } | null;
    StartDate: string | null;
    EndDate: string | null;
    MainDescription: string | null;
    LastActivity: string | null;
}

export interface MatchableAnswers {
    region: string | null;
    projectScope: string | null;
    budget: string | null;
    startDate: string | null;
    endDate: string | null;
}

export type MatchedField = 'projectScope' | 'region' | 'budget' | 'timeline';

export interface MatchScore {
    project: MatchableProject;
    matchedFields: MatchedField[];
}

const MIN_MATCHES = 2;

export const findBestMatch = (
    answers: MatchableAnswers,
    projects: MatchableProject[],
): MatchScore | null => {
    const scores: MatchScore[] = projects
        .map((project) => ({
            project,
            matchedFields: scoreFields(answers, project),
        }))
        .filter((s) => s.matchedFields.length >= MIN_MATCHES);

    if (scores.length === 0) return null;

    scores.sort((a, b) => {
        if (b.matchedFields.length !== a.matchedFields.length) {
            return b.matchedFields.length - a.matchedFields.length;
        }
        return (b.project.LastActivity ?? '').localeCompare(
            a.project.LastActivity ?? '',
        );
    });

    return scores[0] ?? null;
};

const scoreFields = (
    answers: MatchableAnswers,
    project: MatchableProject,
): MatchedField[] => {
    const matched: MatchedField[] = [];
    if (matchesProjectScope(answers.projectScope, project.ProjectScope)) {
        matched.push('projectScope');
    }
    if (matchesRegion(answers.region, project)) {
        matched.push('region');
    }
    if (matchesBudget(answers.budget, project.Budget)) {
        matched.push('budget');
    }
    if (matchesTimeline(answers.startDate, project.StartDate)) {
        matched.push('timeline');
    }
    return matched;
};

// BuildHub rows store ProjectScope as either the slug (`'interior-fitout'`)
// or the human label (`'Interior Fit-Out'`); normalise both sides to the
// slug before comparing.
const SCOPE_SLUG_BY_LABEL: Record<string, string> = Object.fromEntries(
    Object.entries(PROJECT_SCOPE_LABELS).map(([slug, label]) => [
        label.toLowerCase(),
        slug,
    ]),
);

export const normalizeScope = (raw: string | null): string | null => {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const lower = trimmed.toLowerCase();
    if (PROJECT_SCOPE_LABELS[lower]) return lower;
    return SCOPE_SLUG_BY_LABEL[lower] ?? lower;
};

export const matchesProjectScope = (
    answer: string | null,
    project: string | null,
): boolean => {
    const a = normalizeScope(answer);
    const b = normalizeScope(project);
    if (!a || !b) return false;
    return a === b;
};

export const matchesRegion = (
    answer: string | null,
    project: MatchableProject,
): boolean => {
    if (!answer) return false;
    const needle = answer.trim().toLowerCase();
    if (!needle) return false;
    const haystacks = [
        project.LocationData?.City,
        project.LocationData?.Suburb,
        project.LocationData?.Region,
        project.LocationData?.FullAddress,
        project.Location,
    ]
        .filter((v): v is string => typeof v === 'string' && v.length > 0)
        .map((v) => v.toLowerCase());

    return haystacks.some(
        (hay) => hay.includes(needle) || needle.includes(hay),
    );
};

const NOT_SURE_BUDGET = 'Not sure yet';

export const matchesBudget = (
    answer: string | null,
    projectBudget: { Min: number | null; Max: number | null } | null,
): boolean => {
    if (!answer || answer === NOT_SURE_BUDGET || !projectBudget) return false;
    const bucket = BUDGET_OPTIONS.find((opt) => opt.label === answer);
    if (!bucket || bucket.min == null) return false;

    const projMin = projectBudget.Min ?? 0;
    const projMax = projectBudget.Max ?? Number.POSITIVE_INFINITY;
    const bucketMin = bucket.min;
    const bucketMax = bucket.max ?? Number.POSITIVE_INFINITY;

    // Two ranges overlap iff each starts before the other ends.
    return bucketMin <= projMax && projMin <= bucketMax;
};

const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;

export const matchesTimeline = (
    answerStart: string | null,
    projectStart: string | null,
): boolean => {
    if (!answerStart || !projectStart) return false;
    const a = new Date(answerStart).getTime();
    const p = new Date(projectStart).getTime();
    if (Number.isNaN(a) || Number.isNaN(p)) return false;
    return Math.abs(a - p) <= SIXTY_DAYS_MS;
};

/** Maps a BuildHub Min/Max range to the GM bucket whose range contains its midpoint. */
export const budgetBucketFromRange = (
    projectBudget: { Min: number | null; Max: number | null } | null,
): string | null => {
    if (!projectBudget) return null;
    if (projectBudget.Min == null && projectBudget.Max == null) return null;
    const min = projectBudget.Min ?? 0;
    const max = projectBudget.Max ?? min;
    const midpoint = (min + max) / 2;

    const bucket = BUDGET_OPTIONS.find((opt) => {
        if (opt.min == null) return false;
        const bMin = opt.min;
        const bMax = opt.max ?? Number.POSITIVE_INFINITY;
        return midpoint >= bMin && midpoint <= bMax;
    });

    return bucket?.label ?? null;
};
